import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function TestimonialModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", role: "Client", message: "" });
  const [media, setMedia] = useState([]);   // [{type, url}]
  const [mediaType, setMediaType] = useState("photo");
  const [mediaUrl, setMediaUrl] = useState("");
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const addMedia = () => {
    if (!mediaUrl.trim()) return;
    setMedia(m => [...m, { type: mediaType, url: mediaUrl.trim() }]);
    setMediaUrl("");
  };
  const removeMedia = (i) => setMedia(m => m.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) {
      toast.error("Please fill in your name and message");
      return;
    }
    setLoading(true);
    try {
      await api.post("/testimonials", { ...form, media });
      toast.success("Thank you! Your testimonial is awaiting approval.");
      onClose();
      setForm({ name: "", role: "Client", message: "" });
      setMedia([]);
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="testimonial-modal">
      <div className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto border border-[var(--border)]">
        <div className="px-7 py-5 border-b border-[var(--border)] flex items-center justify-between bg-[var(--bg)]">
          <h3 className="font-display text-3xl">Share Your Experience</h3>
          <button onClick={onClose} className="text-2xl" data-testid="testimonial-close">×</button>
        </div>
        <form onSubmit={submit} className="p-7 space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-[var(--muted)] mb-2 block">Your Name *</span>
            <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} required data-testid="testimonial-name"
              className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5 focus:outline-none focus:border-[var(--accent)]" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-[var(--muted)] mb-2 block">Title (e.g. Regular Client)</span>
            <input value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))} data-testid="testimonial-role"
              className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5 focus:outline-none focus:border-[var(--accent)]" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-[var(--muted)] mb-2 block">Your Experience *</span>
            <textarea value={form.message} onChange={e => setForm(f => ({...f, message: e.target.value}))} rows={4} required data-testid="testimonial-message"
              className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-2.5 focus:outline-none focus:border-[var(--accent)]" />
          </label>

          {/* Media (photos / videos) */}
          <div>
            <p className="text-xs uppercase tracking-widest text-[var(--muted)] mb-2">Add Photos / Videos (optional)</p>
            <div className="flex gap-2 mb-2">
              <select value={mediaType} onChange={e => setMediaType(e.target.value)} className="bg-[var(--bg)] border border-[var(--border)] px-3 py-2 text-sm" data-testid="media-type">
                <option value="photo">Photo</option>
                <option value="video">Video</option>
              </select>
              <input value={mediaUrl} onChange={e => setMediaUrl(e.target.value)} placeholder="Public URL (Instagram, Drive, YouTube...)" data-testid="media-url"
                className="flex-1 bg-[var(--bg)] border border-[var(--border)] px-3 py-2 text-sm" />
              <button type="button" onClick={addMedia} className="btn-outline rounded-full px-4 py-2 text-xs uppercase tracking-widest" data-testid="media-add">Add</button>
            </div>
            {media.length > 0 && (
              <ul className="space-y-1" data-testid="media-list">
                {media.map((m, i) => (
                  <li key={i} className="flex items-center justify-between bg-[var(--bg)] px-3 py-2 text-xs border border-[var(--border)]">
                    <span><strong className="uppercase tracking-widest text-[var(--accent)]">{m.type}</strong> · {m.url.slice(0, 60)}{m.url.length > 60 ? "…" : ""}</span>
                    <button type="button" onClick={() => removeMedia(i)} className="text-red-600">×</button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <p className="text-xs text-[var(--muted)] italic">Your testimonial (with photos/videos) will be reviewed by Glazy and then approved by the site administrator before publishing.</p>
          <button type="submit" disabled={loading}
            className="btn-gold w-full rounded-full py-3 text-sm tracking-widest uppercase disabled:opacity-60" data-testid="testimonial-submit">
            {loading ? "Sending..." : "Submit Testimonial"}
          </button>
        </form>
      </div>
    </div>
  );
}
